import * as Sentry from "@sentry/react";
import { useAuth } from "@clerk/clerk-react";
import { Button, Center, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useDeleteGame } from "@/api/fapinstructor/game/delete-game";
import { gamesQueryOptions } from "@/api/fapinstructor/games/get-games";
import { CustomGameCard } from "@/components/custom-game-card";
import { Masonry } from "masonic";
import type { GameSummary } from "@fapinstructor/common";
import { useNavigate } from "@tanstack/react-router";

import { createFileRoute } from "@tanstack/react-router";
import { Page } from "@/components/page";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/_main/my-games",
)({
  loader: (opts) =>
    opts.context.queryClient.ensureInfiniteQueryData(
      gamesQueryOptions({ userId: opts.context.auth.userId }),
    ),
  component: MyGamesRouteComponent,
});

function MyGamesRouteComponent() {
  const { userId } = useAuth();
  const gamesQuery = useSuspenseInfiniteQuery(gamesQueryOptions({ userId }));
  const games = gamesQuery.data.pages.flatMap((p) => p.data);

  return (
    <Page title="Your Custom Games">
      <Title>Your Custom Games</Title>
      <Text mb="xl">These are all the custom games you've created.</Text>
      <Stack>
        <Masonry
          key={crypto.randomUUID()}
          items={games}
          columnGutter={16}
          columnWidth={400}
          render={MasonryCard}
        />
        {gamesQuery.hasNextPage ? (
          <Center>
            <Button
              loading={gamesQuery.isFetchingNextPage}
              onClick={() => {
                gamesQuery.fetchNextPage();
              }}
            >
              Load More
            </Button>
          </Center>
        ) : null}
      </Stack>
    </Page>
  );
}

type MasonryCardProps = { data: GameSummary };

function MasonryCard({ data: game }: MasonryCardProps) {
  const deleteGameMutation = useDeleteGame();
  const navigate = useNavigate();

  return (
    <CustomGameCard
      game={game}
      onEdit={() => {
        navigate({ to: `/game-builder/${game.id}` });
      }}
      onDelete={() => {
        deleteGameMutation.mutate(
          { gameId: game.id },
          {
            onSuccess() {
              notifications.show({
                title: "Successfully deleted game",
                message: "Your game has been successfully deleted.",
                color: "green",
                autoClose: 3000,
              });
            },
            onError(error) {
              Sentry.captureException(error);
              notifications.show({
                title: "An error has occured",
                message:
                  "Failed to delete your game. Please reach out on discord for assistance.",
                color: "red",
                autoClose: 10000,
              });
            },
          },
        );
      }}
    />
  );
}
